<template>
  <div :class="['message', messageClass]">
    <div v-if="message.type === 'bot'" :class="avatarClass">
      <i :class="avatarIcon"></i>
    </div>

    <div 
      class="message-content"
      :class="{ 'error': message.isError, 'status': message.isStatus }"
    >
     <!-- Einfach alle Bot-Nachrichten direkt anzeigen -->
<div>
  <div v-html="messageContent"></div>
</div>

      
      <small class="message-time">{{ formattedTime }}</small>
    </div>

    <div v-if="message.type === 'user'" :class="avatarClass">
      <i :class="avatarIcon"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true,
      validator: (value) => value.content && value.type
    },
    sessionId: {
      type: String,
      default: ''
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      displayedText: '',
      currentIndex: 0,
      typewriterSpeed: 10, // Millisekunden zwischen Zeichen - schneller für bessere UX
      typewriterInterval: null
    }
  },
  computed: {
    isUser() {
      return this.message.type === 'user';
    },
    messageClass() {
      return this.isUser ? 'user-message' : 'bot-message';
    },
    avatarClass() {
      return this.isUser ? 'user-avatar' : 'bot-avatar';
    },
    avatarIcon() {
      return this.isUser ? 'fas fa-user' : 'fas fa-robot';
    },
    formattedTime() {
      if (!this.message.timestamp) return '';
      const date = new Date(this.message.timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    messageContent() {
      return this.parseMarkdown(this.message.content);
    },
    displayedContent() {
      return this.parseMarkdown(this.displayedText);
    },
    // Prüft ob diese Nachricht die neueste Bot-Nachricht ist
    isLatestBotMessage() {
      if (this.message.type !== 'bot') return false;
      
      const botMessages = this.messages.filter(msg => msg.type === 'bot' && !msg.isError && !msg.isStatus);
      const lastBotMessage = botMessages[botMessages.length - 1];
      
      return lastBotMessage && lastBotMessage.id === this.message.id;
    }
  },
  methods: {
   // In der parseMarkdown Methode - sicherstellen dass HTML nicht verändert wird
parseMarkdown(text) {
  if (!text) return '';

  // ── Schritt 1: ALLE HTML-Tags durch sichere Platzhalter ersetzen ─────────
  // WICHTIG: Kein \x00 (Null-Byte) verwenden — der Browser filtert diese
  // beim v-html Rendering heraus, sodass Platzhalter nicht zurückersetzt
  // werden können. Stattdessen eindeutigen String verwenden der NIEMALS
  // im regulären Inhalt vorkommt.
  const PLACEHOLDER_PREFIX = 'SCHUBERTH_HTML_TAG_';
  const PLACEHOLDER_SUFFIX = '_END_TAG';
  const allHtml = [];
  let safe = text;

  // 1a: Komplette Tags mit Inhalt: <div>...</div>, <iframe>...</iframe> usw.
  //     Muss VOR void/self-closing Tags laufen (längste Matches zuerst)
  safe = safe.replace(/<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>[\s\S]*?<\/\1>/g, (match) => {
    const idx = allHtml.length;
    allHtml.push(match);
    return `${PLACEHOLDER_PREFIX}${idx}${PLACEHOLDER_SUFFIX}`;
  });

  // 1b: Void / self-closing Tags: <img ...>, <br>, <hr>, <input .../> usw.
  safe = safe.replace(/<[a-zA-Z][a-zA-Z0-9]*\b[^>]*\/?>/g, (match) => {
    const idx = allHtml.length;
    allHtml.push(match);
    return `${PLACEHOLDER_PREFIX}${idx}${PLACEHOLDER_SUFFIX}`;
  });

  // ── Schritt 2: Markdown-Links konvertieren [Titel](URL) ──────────────────
  let result = safe.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    (match, title, url) => {
      // Direkt als Platzhalter speichern, damit Schritt 3 die URL
      // im href-Attribut nicht nochmal verlinkt
      const tag = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="chat-link ${this.getLinkType(url)}">${title}</a>`;
      const idx = allHtml.length;
      allHtml.push(tag);
      return `${PLACEHOLDER_PREFIX}${idx}${PLACEHOLDER_SUFFIX}`;
    }
  );

  // ── Schritt 3: Nackte URLs verlinken (nur außerhalb von Platzhaltern) ────
  result = result.replace(/(https?:\/\/[^\s<>"']+)/g, (match, url) => {
    // Satzzeichen am URL-Ende entfernen
    const trimmed = url.replace(/[.,;:!?)\]]+$/, '');
    const suffix = url.slice(trimmed.length);
    const title = this.getLinkTitle(trimmed);
    return `<a href="${trimmed}" target="_blank" rel="noopener noreferrer" class="chat-link ${this.getLinkType(trimmed)}">${title}</a>${suffix}`;
  });

  // ── Schritt 4: E-Mails und Telefonnummern verlinken ─────────────────────
  result = result
    .replace(
      /(📧\s*)?([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
      (match, icon, email) => {
        const display = icon ? `📧 ${email}` : email;
        return `<a href="mailto:${email}" class="chat-link contact-link email-link">${display}</a>`;
      }
    )
    .replace(
      /(☎\s*)?(\+?[0-9][0-9()\s-]{8,}[0-9])/g,
      (match, icon, phone) => {
        const cleanPhone = phone.replace(/[^\d+]/g, '');
        const display = icon ? `☎ ${phone.trim()}` : phone.trim();
        return `<a href="tel:${cleanPhone}" class="chat-link contact-link phone-link">${display}</a>`;
      }
    );

  // ── Schritt 5: Alle ursprünglichen HTML-Tags wiederherstellen ───────────
  // Rückwärts iterieren damit keine Index-Kollisionen entstehen
  for (let i = allHtml.length - 1; i >= 0; i--) {
    result = result.split(`${PLACEHOLDER_PREFIX}${i}${PLACEHOLDER_SUFFIX}`).join(allHtml[i]);
  }

  return result;
},

// NEUE METHODE: Prüft ob URL bereits verlinkt ist
isAlreadyLinked(text, url) {
  // Vereinfachte Prüfung - wenn die URL bereits in einem <a> Tag vorkommt
  return text.includes(`href="${url}"`) || text.includes(`href='${url}'`);
},

// Vereinfachte getLinkTitle Methode
getLinkTitle(url) {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    
    // Spezielle Fälle für Schuberth URLs
    if (hostname.includes('dealerlocator.schuberth.com')) {
      return 'Händlersuche';
    }
    if (hostname.includes('shop.schuberth.com')) {
      return 'Online-Shop';
    }
    if (hostname.includes('schuberth.com') || hostname.includes('schuberth.de')) {
      return '🌐 Schuberth Website';
    }
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return '🎥 YouTube Video';
    }
    
    // Generischer Fall
    return `🔗 ${hostname.replace('www.', '')}`;
  } catch (e) {
    // Falls URL-Parsing fehlschlägt
    return `🔗 Link`;
  }
},
getLinkType(url) {
  const urlStr = url.toLowerCase();
  
  if (urlStr.includes('mailto:')) return 'email-link';
  if (urlStr.includes('tel:')) return 'phone-link';
  if (urlStr.includes('dealerlocator') || urlStr.includes('händlersuche')) return 'dealer-link';
  if (urlStr.includes('shop.schuberth') || urlStr.includes('online-shop')) return 'shop-link';
  if (urlStr.includes('youtube.com') || urlStr.includes('youtu.be')) return 'video-link';
  if (urlStr.includes('/motorrad') || urlStr.includes('/produkt')) return 'product-link';
  if (urlStr.includes('schuberth.com') || urlStr.includes('schuberth.de')) return 'homepage-link';
  return 'standard-link';
},

    startTypewriter() {
      if (this.typewriterInterval) {
        clearInterval(this.typewriterInterval);
      }

      this.displayedText = '';
      this.currentIndex = 0;
      const fullText = this.message.content;

      // Nur einmal am Anfang nach unten scrollen
      this.$emit('scroll-to-bottom');

      this.typewriterInterval = setInterval(() => {
        if (this.currentIndex < fullText.length) {
          this.displayedText += fullText.charAt(this.currentIndex);
          this.currentIndex++;
          // KEIN scroll-to-bottom hier — User kann frei scrollen während Antwort tippt
        } else {
          clearInterval(this.typewriterInterval);
          this.typewriterInterval = null;
          // Einmal am Ende scrollen falls User nicht manuell gescrollt hat
          this.$emit('scroll-to-bottom');
        }
      }, this.typewriterSpeed);
    },

    skipTypewriter() {
      if (this.typewriterInterval) {
        clearInterval(this.typewriterInterval);
        this.typewriterInterval = null;
      }
      this.displayedText = this.message.content;
    }
  },
  watch: {
    // Start typewriter effect when message is added and it's the latest bot message
    'message.content': {
      handler(newContent, oldContent) {
        if (this.message.type === 'bot' && 
            !this.message.isError && 
            !this.message.isStatus && 
            !this.message.isTyping &&
            this.isLatestBotMessage &&
            newContent !== oldContent) {
          
          // Kleine Verzögerung für bessere UX
          setTimeout(() => {
            this.startTypewriter();
          }, 100);
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Falls die Nachricht bereits existiert, zeige sofort den vollständigen Text
    if (this.message.type === 'bot' && 
        !this.message.isError && 
        !this.message.isStatus && 
        !this.isLatestBotMessage) {
      this.displayedText = this.message.content;
    }

    // Klick-Listener um Typewriter zu überspringen
    this.$el.addEventListener('click', this.skipTypewriter);
  },
  beforeUnmount() {
    if (this.typewriterInterval) {
      clearInterval(this.typewriterInterval);
    }
    this.$el.removeEventListener('click', this.skipTypewriter);
  },
  emits: ['scroll-to-bottom']
}
</script>

<style scoped>


.message-content :deep(img) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 16px 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
}

/* ── YouTube / Video Embed ─────────────────────────────────────────── */
.message-content :deep(.youtube-embed) {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Seitenverhältnis */
  height: 0;
  overflow: hidden;
  max-width: 100%;
  margin: 16px 0;
  border-radius: 10px;
  background: #000;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.message-content :deep(.youtube-embed iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 10px;
}

/* Responsive Bilder */
@media (max-width: 768px) {
  .message-content :deep(img) {
    margin: 12px 0;
    border-radius: 6px;
  }
}

/* Alternative: Wenn :deep nicht funktioniert, verwenden Sie diesen Ansatz */
.message-content img {
  display: block !important;
  max-width: 100% !important;
  height: auto !important;
  margin: 16px 0 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid #e2e8f0 !important;
}
/* Spezifische Regeln für Tabellen und Listen */
.message-content >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 1rem;
  table-layout: fixed; /* Wichtig für responsive Tabellen */
}

.message-content >>> th,
.message-content >>> td {
  word-break: break-word;
  overflow-wrap: break-word;
  padding: 8px 12px;
}

/* Lange URLs und Wörter brechen */
.message-content >>> a {
  word-break: break-all;
  overflow-wrap: break-word;
}

/* Code-Blöcke und pre-Tags */
.message-content >>> pre,
.message-content >>> code {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  overflow-x: auto;
}

/* Listen */
.message-content >>> ul,
.message-content >>> ol {
  padding-left: 1.5rem;
  word-break: break-word;
}

.message-content >>> li {
  margin-bottom: 0.5rem;
  word-break: break-word;
}

.message-content >>> tr:nth-child(even) {
  background-color: #f8f9fa;
}



.message-content >>> .youtube-embed {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  margin: 12px 0;
}

.message-content >>> .youtube-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* Responsive Tabellen für Mobile */
@media (max-width: 768px) {
  .message-content >>> table {
    font-size: 1rem;
  }
  
}




.message {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  margin-bottom: 20px;
  width: 100%;
  cursor: pointer; /* Zeigt an, dass man klicken kann um zu überspringen */
}

.user-message {
  justify-content: flex-end;
}

.bot-message {
  justify-content: flex-start;
}

/* Avatars */
.user-avatar, .bot-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.user-avatar:hover, .bot-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.user-avatar {
  background: var(--background-light);
  color: var(--border-dark);
}



.bot-avatar {
  background: var(--primary-color) !important;
}


.message-content {
  max-width: 95%;
  flex-shrink: 1;
  padding: 12px 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: clamp(0.95rem, 2vw, 1.05rem);
  line-height: 1.6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  word-wrap: break-word;
  white-space: normal;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  
  /* NEUE REGELN FÜR RESPONSIVE TEXT */
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  
  /* FLEXIBLE BREITE */
  width: fit-content;
  min-width: min-content;
}

.message-content:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.bot-message .message-content {
  background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%);
  color: #0a0a0a;
  border: 1px solid #E2E8F0;
  border-radius: 25px 25px 25px 3px;
}

.user-message .message-content {
  background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%);
  color: #0a0a0a;
  border: 1px solid #E2E8F0;
  border-radius: 25px 25px 3px 25px;
}

.bot-message .message-content::after {
  content: '|';
  animation: blink 1s infinite;
  color: #003366;
  font-weight: bold;
  margin-left: 2px;
}

.bot-message .message-content:not(.typing)::after {
  display: none;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* Timestamp */
.message-time {
  display: block;
  text-align: right;
  font-size: 1rem;
  color: #94A3B8;
  margin-top: 6px;
  font-weight: 400;
}


/* Status Messages */
.message-content.error {
  background: linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%) !important;
  border-color: #F87171 !important;
  color: #DC2626 !important;
}

.message-content.status {
  background: linear-gradient(135deg, #2ca238 0%, #EDEDED 100%);
  border-color: #34D399 !important;
  color: #0a0a0a !important;
}

/* Skip Hint */
.bot-message .message-content::before {

  position: absolute;
  top: -20px;
  left: 0;
  font-size: 1rem;
  color: #6b7280;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.bot-message:hover .message-content::before {
  opacity: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .message-content {
    max-width: 90%;
    font-size: 1rem;
  }
  .user-avatar, .bot-avatar {
    width: 32px;
    height: 32px;
  }
  
  .bot-message .message-content::before {
    font-size: 1rem;
    top: -18px;
  }
}
@media (max-width: 480px) {
  .message-content {
    max-width: 95% !important;
    padding: 10px 12px;
    font-size: 1rem;
  }
}
</style>

<style>

.links-heading {
  font-weight: 600;
  font-size: 1rem;
  color: #374151;
  margin: 16px 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Basis Chat-Link Stile */
.chat-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  transition: all 0.2s ease;
  color: #2563eb;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  gap: 8px;
}

.chat-link:hover {
  background-color: #eff6ff;
  color: #1d4ed8;
  transform: translateX(3px);
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.15);
}

.chat-link::before {
  content: "→";
  font-weight: bold;
  font-size: 1.1em;
  color: inherit;
  transition: transform 0.2s ease;
}

.chat-link:hover::before {
  transform: translateX(2px);
}

/* 🔥 AUTOMATISCHE URL-BASIERTE STYLES - DAS FEHLTE! */
a[href*="dealerlocator"].chat-link {
  color: #7c3aed !important;
  border-color: #e9d5ff !important;
  background: #faf5ff !important;
}

a[href*="dealerlocator"].chat-link:hover {
  background: #f3e8ff !important;
  border-color: #c084fc !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.2);
}

a[href*="shop.schuberth"].chat-link {
  color: #0369a1 !important;
  border-color: #bae6fd !important;
  background: #f0f9ff !important;
}

a[href*="shop.schuberth"].chat-link:hover {
  background: #e0f2fe !important;
  border-color: #7dd3fc !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(3, 105, 161, 0.2);
}

a[href*="youtube"].chat-link,
a[href*="youtu.be"].chat-link {
  color: #dc2626 !important;
  border-color: #fecaca !important;
  background: #fef2f2 !important;
}

a[href*="youtube"].chat-link:hover,
a[href*="youtu.be"].chat-link:hover {
  background: #fee2e2 !important;
  border-color: #f87171 !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

/* E-Mail und Telefon Links */
a[href^="mailto:"].chat-link {
  color: #dc2626 !important;
  border-color: #fecaca !important;
}

a[href^="mailto:"].chat-link:hover {
  background: #fef2f2 !important;
  border-color: #f87171 !important;
}

a[href^="tel:"].chat-link {
  color: #059669 !important;
  border-color: #d1fae5 !important;
}

a[href^="tel:"].chat-link:hover {
  background: #f0fdf4 !important;
  border-color: #34d399 !important;
}

/* Responsive Link Styles */
@media (max-width: 600px) {
  .links-heading {
    font-size: 1rem;
    margin: 12px 0 6px 0;
  }
  
  .chat-link {
    font-size: 1rem;
    padding: 6px 10px;
    margin: 3px 0;
  }
}

.topic-correction-note {
    margin-top: 20px;
    padding: 10px 15px;
    border-left: 5px solid #ffc107;
    background-color: #fff9e6;
    border-radius: 4px;
    font-size: 1em;
    color: #333;
    font-weight: 500;
}
.topic-correction-note strong {
    color: #333;
}

</style>